import {
    ChangeDetectionStrategy,
    Component,
    computed,
    OnDestroy,
    OnInit,
    signal,
    viewChild,
    ViewEncapsulation,
} from "@angular/core";
import { ContactService } from "../../services/contact.service";
import { S25Const } from "../../util/s25-const";
import { TypeManagerDecorator } from "../../main/type.map.service";
import { S25ModalComponent } from "../s25-modal/s25.modal.component";
import { DropDownItem } from "../../pojo/DropDownItem";
import { DropDownPaginatedModel } from "../../pojo/DropDownPaginatedModel";
import { CopySeriesDatabaseProcParams, CopySeriesDatabaseService } from "./copy.series.database.service";

@TypeManagerDecorator("s25-ng-copy-series-database")
@Component({
    selector: "s25-ng-copy-series-database",
    template: `
        @if (init()) {
            <div class="copy-series-database">
                <p class="ngFinePrint c-margin-bottom--quarter">
                    Copy the current instance database onto a target instance. This overwrites all data on the target
                    database.
                </p>

                <label class="ngBlock c-margin-bottom--quarter" for="copyDbSourceInstance">Source instance</label>
                <input
                    id="copyDbSourceInstance"
                    class="c-input c-margin-bottom--single"
                    [value]="sourceInstanceId()"
                    readonly
                    disabled
                />

                <label class="ngBlock c-margin-bottom--quarter">Target instance</label>
                @if (targetOptions().length) {
                    <s25-dropdown-paginated
                        class="copy-series-database-target-dropdown c-margin-bottom--single"
                        [model]="targetDropdownModel"
                        [chosen]="chosenTarget()"
                        [onSelect]="onTargetSelect"
                        [readOnly]="copyInProgress()"
                        [choice]="targetChoice"
                        ariaLabel="Target instance"
                    />
                    <ng-template #targetChoice let-item="item">
                        <span [innerHTML]="item.itemName"></span>
                    </ng-template>
                } @else {
                    <p class="ngFinePrint c-margin-bottom--single">No target instances available on this server.</p>
                }

                <button
                    class="aw-button aw-button--primary c-margin-bottom--single"
                    [disabled]="!canOpenWarning()"
                    (click)="openWarningModal()"
                >
                    Copy database
                </button>

                @if (statusMessage()) {
                    <div
                        class="c-margin-bottom--single cn-alert"
                        [class.cn-alert--success]="statusTone() === 'success'"
                        [class.cn-alert--danger]="statusTone() === 'error'"
                        [class.cn-alert--warning]="statusTone() === 'warning'"
                        role="alert"
                    >
                        {{ statusMessage() }}
                    </div>
                }

                <s25-ng-modal #warningModal title="Overwrite target database" [type]="'continue'" [size]="'lg'">
                    <ng-template #s25ModalBody>
                        <p class="c-margin-bottom--single">
                            You are about to copy <strong>{{ sourceInstanceId() }}</strong> onto
                            <strong>{{ selectedTargetInstance() }}</strong>
                            (database <strong>{{ selectedTargetDbName() }}</strong
                            >).
                        </p>
                        <p class="c-margin-bottom--single">
                            This will permanently overwrite the target database. The operation may take a long time and
                            blocks other copies involving the same source or target database until it finishes.
                        </p>
                        <p>This action cannot be undone.</p>
                    </ng-template>
                </s25-ng-modal>

                <s25-ng-modal #typeConfirmModal title="Type target instance to confirm" [type]="'custom'" [size]="'md'">
                    <ng-template #s25ModalBody>
                        <p class="c-margin-bottom--single">
                            This will permanently replace all data on the target instance.
                        </p>
                        <p class="c-margin-bottom--quarter">
                            Type <strong>{{ selectedTargetInstance() }}</strong> to confirm.
                        </p>
                        <input
                            class="c-input"
                            type="text"
                            [ngModel]="typeConfirmText()"
                            (ngModelChange)="typeConfirmText.set($event)"
                        />
                    </ng-template>
                    <ng-template #s25ModalFooter>
                        <div class="buttons">
                            <button class="aw-button aw-button--outline" (click)="closeTypeConfirmModal(false)">
                                Cancel
                            </button>
                            <button
                                class="aw-button aw-button--danger"
                                [disabled]="!canSubmitTypeConfirm()"
                                (click)="closeTypeConfirmModal(true)"
                            >
                                Start copy
                            </button>
                        </div>
                    </ng-template>
                </s25-ng-modal>
            </div>
        }
    `,
    styles: `
        .copy-series-database {
            .c-input,
            .copy-series-database-target-dropdown {
                width: 60%;
                max-width: 400px;
            }
            .buttons {
                display: flex;
                gap: 0.5em;
                justify-content: flex-end;
                padding: 1em;
            }
        }
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.Emulated,
    standalone: false,
})
export class S25CopySeriesDatabaseComponent implements OnInit, OnDestroy {
    readonly warningModal = viewChild<S25ModalComponent<boolean>>("warningModal");
    readonly typeConfirmModal = viewChild<S25ModalComponent<boolean>>("typeConfirmModal");

    readonly init = signal(false);
    readonly sourceInstanceId = signal(S25Const.instanceId || "");
    readonly targetOptions = signal<CopySeriesDatabaseProcParams[]>([]);
    readonly chosenTarget = signal<DropDownItem | undefined>(undefined);
    readonly typeConfirmText = signal("");
    readonly copyInProgress = signal(false);
    readonly statusMessage = signal("");
    readonly statusTone = signal<"success" | "error" | "warning" | "info">("info");

    readonly targetDropdownModel: DropDownPaginatedModel = {
        items: [],
        itemNameProp: "itemName",
        placeholder: "Select target instance",
        emptyText: "No matching target instances",
        searchEnabled: true,
        pageSize: 10_000,
        noItemIdForGet: true,
    };

    readonly selectedTargetInstance = computed(() => (this.chosenTarget()?.val as string) || "");

    readonly selectedTargetDbName = computed(() => {
        const targetInstance = this.selectedTargetInstance();
        const match = this.targetOptions().find((option) => option.targetInstanceId === targetInstance);
        return match?.targetDb || "";
    });

    readonly canOpenWarning = computed(
        () => !!this.selectedTargetInstance() && this.targetOptions().length > 0 && !this.copyInProgress(),
    );

    readonly canSubmitTypeConfirm = computed(() => this.typeConfirmText().trim() === this.selectedTargetInstance());

    private pollTimer: ReturnType<typeof setInterval> | undefined;

    async ngOnInit() {
        const username = await ContactService.getCurrentUsername();
        if (username !== "service25") {
            return;
        }
        await this.loadTargetOptions();
        this.init.set(true);
    }

    ngOnDestroy() {
        this.stopPolling();
    }

    optionLabel(option: CopySeriesDatabaseProcParams): string {
        if (option.targetDb && option.targetDb !== option.targetInstanceId) {
            return `${option.targetInstanceId} (${option.targetDb})`;
        }
        return option.targetInstanceId || "";
    }

    readonly onTargetSelect = (item?: DropDownItem) => {
        this.chosenTarget.set(item);
        this.typeConfirmText.set("");
        this.statusMessage.set("");
    };

    private toDropdownItem(option: CopySeriesDatabaseProcParams): DropDownItem {
        return {
            val: option.targetInstanceId,
            txt: this.optionLabel(option),
            itemId: option.targetInstanceId,
            itemName: this.optionLabel(option),
        };
    }

    async openWarningModal() {
        if (!this.canOpenWarning()) {
            return;
        }
        const continued = await this.warningModal()?.open();
        if (!continued) {
            return;
        }
        this.typeConfirmText.set("");
        const confirmed = await this.typeConfirmModal()?.open();
        if (!confirmed) {
            this.typeConfirmText.set("");
            return;
        }
        await this.startCopy();
    }

    closeTypeConfirmModal(confirmed: boolean) {
        this.typeConfirmModal()?.close(confirmed);
    }

    private async loadTargetOptions() {
        try {
            const options = await CopySeriesDatabaseService.listInstances();
            this.targetOptions.set(options || []);
            this.targetDropdownModel.items = (options || []).map((option) => this.toDropdownItem(option));
        } catch (caught) {
            this.statusTone.set("error");
            this.statusMessage.set(CopySeriesDatabaseService.formatError(caught));
        }
    }

    private async startCopy() {
        this.copyInProgress.set(true);
        this.statusTone.set("info");
        this.statusMessage.set("Starting database copy...");
        try {
            const response = await CopySeriesDatabaseService.startCopy(this.selectedTargetInstance());
            this.statusTone.set("info");
            this.statusMessage.set(`Copy in progress (job ${response.jobId}).`);
            this.startPolling(response.jobId);
        } catch (caught) {
            this.copyInProgress.set(false);
            this.statusTone.set(caught?.status === 409 ? "warning" : "error");
            this.statusMessage.set(CopySeriesDatabaseService.formatError(caught));
        }
    }

    private startPolling(jobId: string) {
        this.stopPolling();
        const poll = async () => {
            try {
                const job = await CopySeriesDatabaseService.getJobStatus(jobId);
                if (job.status === "running") {
                    this.statusTone.set("info");
                    this.statusMessage.set(job.message || `Copy in progress (job ${jobId}).`);
                    return;
                }
                this.stopPolling();
                this.copyInProgress.set(false);
                if (job.status === "completed") {
                    this.statusTone.set("success");
                    this.statusMessage.set(job.message || "Database copy completed successfully.");
                } else {
                    this.statusTone.set("error");
                    this.statusMessage.set(job.message || "Database copy failed.");
                }
            } catch (caught) {
                this.stopPolling();
                this.copyInProgress.set(false);
                this.statusTone.set("error");
                this.statusMessage.set(CopySeriesDatabaseService.formatError(caught));
            }
        };
        void poll();
        this.pollTimer = setInterval(() => void poll(), 5000);
    }

    private stopPolling() {
        if (this.pollTimer) {
            clearInterval(this.pollTimer);
            this.pollTimer = undefined;
        }
    }
}
